import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { gmudService, aprovadorService } from '../services/api';
import { CreateGMUDData, Aprovador } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { mensagemDeErro } from '../utils/erroApi';
import GMUDForm, { ArquivosPendentes } from '../components/gmud-form/GMUDForm';

const GMUDCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [aprovadores, setAprovadores] = useState<Aprovador[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const carregarAprovadores = async () => {
      try {
        setAprovadores(await aprovadorService.list(true)); // somente aprovadores ativos
      } catch (error) {
        toast.error('Erro ao carregar aprovadores');
        console.error('Erro:', error);
      }
    };
    carregarAprovadores();
  }, []);

  // Evidências e anexos do script são enviados depois que a GMUD existe
  const enviarArquivos = async (
    gmudId: number,
    arquivos: File[],
    categoria: 'evidencia' | 'script_banco'
  ) => {
    if (arquivos.length === 0) return;
    const rotulo = categoria === 'evidencia' ? 'evidência' : 'script de banco de dados';
    try {
      const formData = new FormData();
      arquivos.forEach(file => formData.append('evidencia_arquivos', file));
      await api.post(`/upload/evidencias/${gmudId}`, formData, {
        params: { categoria },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`${arquivos.length} arquivo(s) de ${rotulo} enviado(s) com sucesso!`);
    } catch (uploadError: unknown) {
      console.error('Erro no upload dos arquivos:', uploadError);
      toast.error(
        mensagemDeErro(uploadError, `GMUD criado, mas houve erro no upload dos arquivos de ${rotulo}`)
      );
    }
  };

  const onSalvar = async (dados: CreateGMUDData, arquivos: ArquivosPendentes, enviar: boolean) => {
    setSubmitting(true);
    let gmudId: number | null = null;
    try {
      const result = await gmudService.create(dados);
      gmudId = result.id;

      await enviarArquivos(result.id, arquivos.evidencia, 'evidencia');
      await enviarArquivos(result.id, arquivos.script, 'script_banco');

      if (!enviar) {
        toast.success('Rascunho salvo com sucesso!');
        navigate('/gmuds');
        return;
      }

      await api.patch(`/gmuds/${result.id}/status`, { status: 'pendente' });
      toast.success('GMUD criada e enviada para aprovação!');
      navigate(`/gmuds/${result.id}`);
    } catch (error: unknown) {
      if (gmudId) {
        // A GMUD já existe como rascunho: leva para o detalhe para tentar o envio de novo
        toast.error(
          `GMUD salva como rascunho, mas não foi enviada: ${mensagemDeErro(
            error,
            'erro ao enviar para aprovação'
          )}`,
          { duration: 8000 }
        );
        navigate(`/gmuds/${gmudId}/editar`);
      } else {
        toast.error(mensagemDeErro(error, 'Erro ao criar GMUD'));
      }
      console.error('Erro:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/template/template', { responseType: 'blob' });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template-gmud.docx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Template baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar template:', error);
      toast.error('Erro ao baixar template');
    }
  };

  return (
    <div className='space-y-6'>
      <div className='mx-auto flex max-w-6xl items-center justify-between gap-4'>
        <div className='flex items-center space-x-4'>
          <button
            type='button'
            onClick={() => navigate('/gmuds')}
            aria-label='Voltar para a lista'
            className='p-2 text-gray-400 transition-colors hover:text-gray-600'
          >
            <ArrowLeft className='h-5 w-5' />
          </button>
          <div>
            <h1 className='text-3xl font-bold text-gray-900'>Nova GMUD</h1>
            <p className='mt-1 text-gray-600'>Responda cada etapa, do quê até a revisão</p>
          </div>
        </div>

        <button
          type='button'
          onClick={downloadTemplate}
          className='flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700'
        >
          <Download className='h-4 w-4' />
          <span className='hidden sm:inline'>Baixar Template DOCX</span>
        </button>
      </div>

      <GMUDForm
        modo='criar'
        aprovadores={aprovadores}
        responsavelFixo={user?.email || ''}
        salvando={submitting}
        onSalvar={onSalvar}
        onCancelar={() => navigate('/gmuds')}
      />
    </div>
  );
};

export default GMUDCreate;
